<template>
  <div class="animated-background">
    <!-- Canvas for particle animation -->
    <canvas ref="canvasRef" class="particle-canvas"></canvas>
    
    <!-- Floating gradient orbs -->
    <div class="floating-orbs">
      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>
      <div class="orb orb-3"></div>
      <div class="orb orb-4"></div>
    </div>
    
    <!-- Grid pattern overlay -->
    <div class="grid-overlay"></div>
    
    <!-- Wave pattern -->
    <div class="wave-pattern">
      <div class="wave wave-1"></div>
      <div class="wave wave-2"></div>
      <div class="wave wave-3"></div>
    </div>

    <!-- Rotating rings -->
    <div class="rotating-rings">
      <div class="ring ring-1"></div>
      <div class="ring ring-2"></div>
      <div class="ring ring-3"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

// Props 정의
defineProps({
  backgroundColor: {
    type: String,
    default: 'linear-gradient(135deg, #001850 0%, #002580 50%, #0033A0 100%)'
  }
})

const canvasRef = ref(null)
let animationId = null
let particles = []

// Particle class
class Particle {
      constructor(canvas) {
        this.canvas = canvas
        this.x = Math.random() * canvas.width
        this.y = Math.random() * canvas.height
        this.size = Math.random() * 3 + 1
        this.speedX = Math.random() * 0.5 - 0.25
        this.speedY = Math.random() * 0.5 - 0.25
        
        // 단색 그레이 톤으로 변경
        this.color = 'rgba(128, 128, 128, 0.3)'
        this.opacity = Math.random() * 0.5 + 0.2
      }
  
  update() {
    this.x += this.speedX
    this.y += this.speedY
    
    // Wrap around edges
    if (this.x > this.canvas.width) this.x = 0
    if (this.x < 0) this.x = this.canvas.width
    if (this.y > this.canvas.height) this.y = 0
    if (this.y < 0) this.y = this.canvas.height
  }
  
  draw(ctx) {
    ctx.fillStyle = this.color
    ctx.beginPath()
    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2)
    ctx.fill()
  }
}

const initCanvas = () => {
  const canvas = canvasRef.value
  if (!canvas) return
  
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  
  // Set canvas size
  const resizeCanvas = () => {
    canvas.width = window.innerWidth
    canvas.height = window.innerHeight
  }
  
  resizeCanvas()
  window.addEventListener('resize', resizeCanvas)
  
  // Create particles
  particles = []
  const particleCount = 80
  for (let i = 0; i < particleCount; i++) {
    particles.push(new Particle(canvas))
  }
  
  // Animation loop
  const animate = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    
    // Draw particles and connections
    particles.forEach((particle, index) => {
      particle.update()
      particle.draw(ctx)
      
      // Draw lines to nearby particles
      for (let j = index + 1; j < particles.length; j++) {
        const dx = particles[j].x - particle.x
        const dy = particles[j].y - particle.y
        const distance = Math.sqrt(dx * dx + dy * dy)
        
        if (distance < 150) {
          ctx.strokeStyle = `rgba(128, 128, 128, ${0.1 * (1 - distance / 150)})`
          ctx.lineWidth = 1
          ctx.beginPath()
          ctx.moveTo(particle.x, particle.y)
          ctx.lineTo(particles[j].x, particles[j].y)
          ctx.stroke()
        }
      }
    })
    
    animationId = requestAnimationFrame(animate)
  }
  
  animate()
  
  // Cleanup function
  return () => {
    window.removeEventListener('resize', resizeCanvas)
    if (animationId) {
      cancelAnimationFrame(animationId)
    }
  }
}

onMounted(() => {
  const cleanup = initCanvas()
  
  // Store cleanup function for onUnmounted
  onUnmounted(() => {
    if (cleanup) cleanup()
  })
})
</script>

<style scoped>
.animated-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0; /* 섹션들 아래에 표시되도록 z-index 설정 */
  overflow: hidden;
  /* 🔧 UI/UX 개선: props로 배경 색상 제어 */
  background: v-bind(backgroundColor);
}

.particle-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.6;
}

.floating-orbs {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.5;
}

.orb-1 {
  top: -200px;
  left: -200px;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, #92CDDC 0%, rgba(146, 205, 220, 0.3) 50%, transparent 70%);
  animation: float 20s ease-in-out infinite;
}

.orb-2 {
  bottom: -300px;
  right: -200px;
  width: 700px;
  height: 700px;
  background: radial-gradient(circle, #C4961A 0%, rgba(196, 150, 26, 0.3) 50%, transparent 70%);
  animation: float-delayed 25s ease-in-out infinite;
  animation-delay: -7s;
}

.orb-3 {
  top: 50%;
  left: 50%;
  width: 800px;
  height: 800px;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle, rgba(146, 205, 220, 0.7) 0%, rgba(146, 205, 220, 0.2) 50%, transparent 70%);
  animation: float-slow 30s ease-in-out infinite;
  animation-delay: -14s;
}

.orb-4 {
  top: 20%;
  right: 10%;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(0, 51, 160, 0.4) 0%, transparent 70%);
  animation: float 20s ease-in-out infinite;
  animation-delay: -21s;
}

.grid-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.1;
  background-image: 
    linear-gradient(rgba(146, 205, 220, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(146, 205, 220, 0.1) 1px, transparent 1px);
  background-size: 60px 60px;
  animation: gridShift 30s linear infinite;
}

@keyframes gridShift {
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(60px, 60px);
  }
}

/* Wave pattern */
.wave-pattern {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  opacity: 0.15;
}

.wave {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 200%;
  height: 100%;
  background: linear-gradient(180deg, transparent 0%, rgba(146, 205, 220, 0.3) 50%, rgba(146, 205, 220, 0.5) 100%);
  animation: waveMove 15s ease-in-out infinite;
}

.wave-1 {
  animation-delay: 0s;
  animation-duration: 20s;
}

.wave-2 {
  animation-delay: -5s;
  animation-duration: 25s;
  opacity: 0.7;
}

.wave-3 {
  animation-delay: -10s;
  animation-duration: 30s;
  opacity: 0.5;
}

@keyframes waveMove {
  0% {
    transform: translateX(0) translateY(0);
  }
  50% {
    transform: translateX(-25%) translateY(-20px);
  }
  100% {
    transform: translateX(-50%) translateY(0);
  }
}

/* Rotating rings */
.rotating-rings {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.ring {
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 50%;
  border: 2px solid rgba(146, 205, 220, 0.2);
  animation: rotateRing 30s linear infinite;
}

.ring-1 {
  width: 300px;
  height: 300px;
  margin: -150px 0 0 -150px;
  animation-duration: 25s;
  border-color: rgba(146, 205, 220, 0.3);
}

.ring-2 {
  width: 500px;
  height: 500px;
  margin: -250px 0 0 -250px;
  animation-duration: 35s;
  animation-direction: reverse;
  border-color: rgba(196, 150, 26, 0.2);
}

.ring-3 {
  width: 700px;
  height: 700px;
  margin: -350px 0 0 -350px;
  animation-duration: 45s;
  border-color: rgba(0, 51, 160, 0.15);
}

@keyframes rotateRing {
  0% {
    transform: rotate(0deg) scale(1);
    opacity: 0.3;
  }
  50% {
    transform: rotate(180deg) scale(1.1);
    opacity: 0.6;
  }
  100% {
    transform: rotate(360deg) scale(1);
    opacity: 0.3;
  }
}

/* Animation keyframes */
@keyframes float {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  25% {
    transform: translate(100px, -80px) scale(1.2) rotate(45deg);
  }
  50% {
    transform: translate(-50px, 100px) scale(0.9) rotate(90deg);
  }
  75% {
    transform: translate(-120px, -50px) scale(1.1) rotate(135deg);
  }
}

@keyframes float-delayed {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(-90px, 70px) scale(1.15) rotate(-60deg);
  }
  66% {
    transform: translate(80px, -90px) scale(0.85) rotate(-120deg);
  }
}

@keyframes float-slow {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
  }
  20% {
    transform: translate(calc(-50% + 60px), calc(-50% - 40px)) scale(1.1) rotate(30deg);
  }
  40% {
    transform: translate(calc(-50% - 70px), calc(-50% + 60px)) scale(0.9) rotate(60deg);
  }
  60% {
    transform: translate(calc(-50% + 50px), calc(-50% + 70px)) scale(1.05) rotate(90deg);
  }
  80% {
    transform: translate(calc(-50% - 60px), calc(-50% - 50px)) scale(0.95) rotate(120deg);
  }
}
</style>
